import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, Staff } from '../services/auth';

interface AuthContextType {
  user: Staff | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  setUser: (user: Staff | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      
      // Refresh CSRF token after successful authentication check
      try {
        await authService.refreshCSRFToken();
      } catch (csrfError) {
        console.error('Failed to refresh CSRF token:', csrfError);
      }
    } catch (error) {
      // Silently handle error - user is not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    setUser(response.data);
    
    // Store CSRF token from response in sessionStorage for cross-domain support
    if (response.data.csrfToken) {
      sessionStorage.setItem('csrfToken', response.data.csrfToken);
    }
    
    // Also try to refresh CSRF token (for same-domain scenarios)
    try {
      await authService.refreshCSRFToken();
    } catch (csrfError) {
      console.error('Failed to refresh CSRF token after login:', csrfError);
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    // Clear CSRF token from sessionStorage
    sessionStorage.removeItem('csrfToken');
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};